(() => {
  const selectors = {
    shell: '[data-libra-cart-shell]',
    drawer: '[data-libra-cart-drawer]',
    close: '[data-libra-cart-close]',
    title: '[data-libra-cart-title]',
    progress: '[data-libra-cart-progress]',
    body: '[data-libra-cart-body]',
    loading: '[data-libra-cart-loading]',
    items: '[data-libra-cart-items]',
    recs: '[data-libra-cart-recommendations]',
    empty: '[data-libra-cart-empty]',
    footer: '[data-libra-cart-footer]',
    subtotal: '[data-libra-cart-subtotal]',
    subtotalCompare: '[data-libra-cart-subtotal-compare]',
    totalSaving: '[data-libra-cart-total-saving]',
    checkout: '[data-libra-cart-checkout]',
    checkoutText: '[data-libra-cart-checkout-text]',
    subtotalLabel: '[data-libra-cart-subtotal-label]',
    shippingLabel: '[data-libra-cart-shipping-label]',
    shippingText: '[data-libra-cart-shipping-text]',
    delivery: '[data-libra-cart-delivery]',
    returns: '[data-libra-cart-returns]',
    emptyTitle: '[data-libra-cart-empty-title]',
    emptyLink: '[data-libra-cart-empty-link]',
  };

  const copy = {
    en: {
      title: 'YOUR BAG',
      freeShipping: 'FREE UK STANDARD SHIPPING',
      save: 'You save {amount}',
      subtotal: 'Subtotal:',
      shipping: 'Shipping:',
      shippingText: 'Free',
      shippingFree: 'Free',
      delivery: 'Tracked UK delivery',
      returns: 'Easy 30-day returns',
      checkout: 'CHECKOUT SECURELY',
      emptyTitle: 'Your bag is empty',
      emptyLink: 'Continue shopping',
      recTitle: 'MADE FOR YOU',
      add: '+ Add',
      size: 'Size',
      addToBag: 'Add to bag',
      selectSize: 'Select a size',
      loading: 'Loading cart...',
    },
  };

  const state = {
    cart: null,
    products: null,
    openedBy: null,
    busy: false,
    pendingUpsell: null,
    selectedUpsellVariantId: null,
  };

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const locale = () => 'en';
  const country = () => (window.Shopify && window.Shopify.country) || 'GB';
  const t = (key) => {
    if (country() === 'US') {
      if (key === 'freeShipping') return 'FREE US SHIPPING';
      if (key === 'delivery') return 'Tracked delivery';
    }
    return copy[locale()][key] || copy.en[key] || key;
  };

  const ajaxUrlCandidates = (path) => {
    const cleanPath = path.replace(/^\//, '');
    const localized = `${root()}${cleanPath}`;
    const fallback = `/${cleanPath}`;
    return localized === fallback ? [localized] : [localized, fallback];
  };

  const fetchJson = async (paths, options = {}) => {
    const candidates = Array.isArray(paths) ? paths : ajaxUrlCandidates(paths);
    let lastError = null;

    for (const url of candidates) {
      try {
        const response = await fetch(url, options);
        const contentType = response.headers.get('content-type') || '';
        if (!response.ok || (!contentType.includes('json') && !contentType.includes('javascript'))) {
          lastError = new Error(`Request failed: ${url}`);
          continue;
        }
        return await response.json();
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error('Request failed');
  };

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || state.cart?.currency || 'GBP',
      }).format((cents || 0) / 100);
    } catch (_error) {
      return `${((cents || 0) / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  };

  const centsFromShopifyPrice = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return value > 999 ? Math.round(value) : Math.round(value * 100);
    const normalized = String(value).replace(',', '.').replace(/[^\d.]/g, '');
    const amount = Number(normalized) || 0;
    return !normalized.includes('.') && amount > 999 ? Math.round(amount) : Math.round(amount * 100);
  };

  const variantCompareAt = (products, variantId) => {
    if (!variantId) return 0;
    for (const product of products || []) {
      const variant = (product.variants || []).find((candidate) => Number(candidate.id) === Number(variantId));
      if (variant) return centsFromShopifyPrice(variant.compare_at_price);
    }
    return 0;
  };

  const originalCartTotal = (cart, products = []) =>
    cart.items.reduce((total, item) => {
      const linePrice = item.final_line_price || item.line_price || 0;
      const catalogCompareAt = variantCompareAt(products, item.variant_id);
      const catalogOriginalLine = catalogCompareAt > 0 ? catalogCompareAt * item.quantity : 0;
      const originalLinePrice = Math.max(
        cart.original_total_price && cart.items.length === 1 ? cart.original_total_price : 0,
        item.original_line_price || 0,
        item.original_price * item.quantity || 0,
        catalogOriginalLine,
        linePrice
      );
      return total + originalLinePrice;
    }, 0);

  const shell = () => document.querySelector(selectors.shell);
  const qs = (selector) => shell()?.querySelector(selector);

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const firstAvailableVariant = (product) =>
    (product.variants || []).find((variant) => variant.available !== false) || product.variants?.[0];

  const productByHandle = (products, handle) => (products || []).find((product) => product.handle === handle);

  const sizeOptionPosition = (product) => {
    const options = product?.options || [];
    const option = options.find((candidate) => String(candidate.name || '').toLowerCase() === 'size');
    return option ? Number(option.position || 1) : 1;
  };

  const variantSizeValue = (product, variant) => {
    const position = sizeOptionPosition(product);
    return variant?.[`option${position}`] || variant?.title || '';
  };

  const sizeVariantsForProduct = (product) => {
    const seen = new Set();
    return (product?.variants || [])
      .map((variant) => ({
        id: variant.id,
        size: variantSizeValue(product, variant),
        available: variant.available !== false,
      }))
      .filter((entry) => entry.size && entry.size !== 'Default Title')
      .filter((entry) => {
        if (seen.has(entry.size)) return false;
        seen.add(entry.size);
        return true;
      });
  };

  const currentLineSize = (item) =>
    (item.options_with_values || []).find((option) => String(option.name || '').toLowerCase() === 'size')?.value ||
    (item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '');

  const sizeSelectHtml = (item, products) => {
    const product = productByHandle(products, item.handle);
    const sizes = sizeVariantsForProduct(product);
    const currentSize = currentLineSize(item);
    if (!product || !sizes.length || !currentSize) return '';

    return `
      <label class="libra-cart-size-control">
        <span>${escapeHtml(t('size'))}:</span>
        <select data-libra-cart-size-select>
          ${sizes
            .map(
              (entry) => `
                <option value="${entry.id}" ${entry.size === currentSize ? 'selected' : ''} ${
                  entry.available ? '' : 'disabled'
                }>
                  ${escapeHtml(entry.size)}
                </option>
              `
            )
            .join('')}
        </select>
      </label>
    `;
  };

  const normalizeProductImage = (product) =>
    product.images?.[0]?.src || product.image?.src || product.featured_image || '';

  const normalizedProductType = (source) => String(source?.product_type || '').trim().toLowerCase();

  const studioHandles = new Set([
    'turquoise-fluo-wide-leg-set',
    'light-blue-contrast-drawcord-joggers',
    'monaco-applique-zip-sweatshirt',
    'chocolate-wide-leg-joggers',
    'butter-yellow-tie-front-joggers',
  ]);

  const searchableProductText = (source) =>
    [source?.handle, source?.title, source?.product_title, source?.product_type]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

  const productCategory = (source) => {
    const text = searchableProductText(source);
    if (
      studioHandles.has(source?.handle) ||
      /(jogger|tracksuit|track suit|sweatshirt|hoodie|drawcord|wide[- ]leg set|monaco applique)/.test(text)
    ) {
      return 'studio';
    }
    if (/(^|\s)(top|tops|corset|blouse|shirt)(\s|$)/.test(text)) return 'tops';
    if (/(dress|robe)/.test(text)) return 'dresses';
    if (/(skirt|jupe)/.test(text)) return 'skirts';
    if (/(trouser|pant|pantalon)/.test(text)) return 'trousers';
    if (/(bag|sac|accessor|jewellery|jewelry)/.test(text)) return 'accessories';
    if (/(^|\s)(set|ensemble)(\s|$)/.test(text)) return 'sets';
    return normalizedProductType(source) || 'all';
  };

  const categoryTitle = (category) =>
    ({
      studio: 'ALBA STUDIO',
      tops: 'TOPS',
      dresses: 'DRESSES',
      skirts: 'SKIRTS',
      trousers: 'TROUSERS',
      accessories: 'ACCESSORIES',
      sets: 'SETS',
    })[category] || t('recTitle');

  const cartRecommendationCategory = (cart, products) => {
    const cartProducts = cart.items
      .map((item) => productByHandle(products, item.handle) || item)
      .filter(Boolean);
    return cartProducts.find((product) => productCategory(product) === 'studio')
      ? 'studio'
      : productCategory(cartProducts[0]);
  };

  const dedupeProducts = (products) => {
    const seen = new Set();
    return products.filter((product) => {
      const key = product.id || product.handle;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const loadProducts = async () => {
    if (state.products) return state.products;

    try {
      const allProducts = [];
      let sinceId = 0;

      for (let page = 0; page < 10; page += 1) {
        const suffix = `products.json?limit=250${sinceId ? `&since_id=${sinceId}` : ''}`;
        const data = await fetchJson(
          [`${window.location.origin}/${suffix}`, `${window.location.origin}${root()}${suffix}`],
          { headers: { Accept: 'application/json' } }
        );
        const products = Array.isArray(data.products) ? data.products : [];
        allProducts.push(...products);
        if (products.length < 250) break;
        sinceId = products[products.length - 1]?.id || sinceId;
        if (!sinceId) break;
      }

      state.products = dedupeProducts(allProducts);
      return state.products;
    } catch (_error) {
      state.products = [];
      return state.products;
    }
  };

  const getCart = async () => {
    state.cart = await fetchJson('cart.js', {
      headers: { Accept: 'application/json' },
    });
    return state.cart;
  };

  const changeLine = async (key, quantity) => {
    await fetchJson('cart/change.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: key, quantity }),
    });
    await refreshCart();
  };

  const addVariant = async (variantId) => {
    if (!variantId || state.busy) return;
    state.busy = true;
    try {
      await fetchJson('cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });
      await refreshCart();
    } finally {
      state.busy = false;
    }
  };

  const switchLineVariant = async (item, variantId) => {
    if (!item || !variantId || Number(item.variant_id) === Number(variantId) || state.busy) return;
    state.busy = true;
    try {
      await fetchJson('cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: item.quantity }] }),
      });
      await fetchJson('cart/change.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });
      await refreshCart();
    } finally {
      state.busy = false;
    }
  };

  const updateCartBubble = (cart) => {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    const iconLink = document.querySelector('#cart-icon-bubble');
    if (!iconLink) return;

    if (!cart.item_count) {
      bubble?.remove();
      return;
    }

    if (bubble) {
      const visible = bubble.querySelector('[aria-hidden="true"]');
      if (visible) visible.textContent = cart.item_count < 100 ? cart.item_count : '';
      return;
    }

    iconLink.insertAdjacentHTML(
      'beforeend',
      `<div class="cart-count-bubble"><span aria-hidden="true">${cart.item_count}</span></div>`
    );
  };

  const renderProgress = (cart) => {
    const progress = qs(selectors.progress);
    if (!progress) return;

    progress.innerHTML = `
      <div><strong>${escapeHtml(t('freeShipping'))}</strong></div>
      <div class="libra-cart-progress-track" aria-hidden="true">
        <span class="libra-cart-progress-fill" style="--libra-cart-progress: 100%"></span>
      </div>
    `;
  };

  const renderItems = (cart, products = []) => {
    const container = qs(selectors.items);
    if (!container) return;

    container.innerHTML = cart.items
      .map((item) => {
        const image = item.image || item.featured_image?.url || '';
        const sizeControl = sizeSelectHtml(item, products);
        const options = (item.options_with_values || [])
          .filter((option) => option.value && option.value !== 'Default Title')
          .filter((option) => !(sizeControl && option.name.toLowerCase() === 'size'))
          .map((option) => {
            const label = option.name.toLowerCase() === 'size' ? t('size') : option.name;
            return `<p class="libra-cart-option">${escapeHtml(label)}: ${escapeHtml(option.value)}</p>`;
          })
          .join('');

        const linePrice = item.final_line_price || item.line_price || 0;
        const catalogCompareAt = variantCompareAt(products, item.variant_id);
        const catalogOriginalLine = catalogCompareAt > 0 ? catalogCompareAt * item.quantity : 0;
        const originalLinePrice = Math.max(
          item.original_line_price || 0,
          item.original_price * item.quantity || 0,
          catalogOriginalLine,
          linePrice
        );
        const saving = Math.max(0, originalLinePrice - linePrice);
        const compare = saving
          ? `<span class="libra-cart-compare">${money(originalLinePrice, cart.currency)}</span>`
          : '';
        const savingLine = saving
          ? `<span class="libra-cart-line-saving">${escapeHtml(t('save').replace('{amount}', money(saving, cart.currency)))}</span>`
          : '';

        return `
          <article class="libra-cart-item" data-libra-cart-line="${escapeHtml(item.key)}">
            <a href="${escapeHtml(item.url)}" tabindex="-1">
              ${
                image
                  ? `<img class="libra-cart-item-image" src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
                  : `<span class="libra-cart-item-image" aria-hidden="true"></span>`
              }
            </a>
            <div class="libra-cart-item-main">
              <h3 class="libra-cart-item-title">${escapeHtml(item.product_title || item.title)}</h3>
              <p class="libra-cart-item-price${saving ? ' has-discount' : ''}"><span class="libra-cart-sale-price">${money(linePrice, cart.currency)}</span>${compare}</p>
              ${savingLine}
              ${sizeControl}
              ${options}
              <div class="libra-cart-qty" aria-label="Quantity">
                <button type="button" data-libra-cart-qty="-1" aria-label="Decrease quantity">−</button>
                <input type="number" min="0" step="1" value="${item.quantity}" data-libra-cart-qty-input aria-label="Quantity">
                <button type="button" data-libra-cart-qty="1" aria-label="Increase quantity">+</button>
              </div>
            </div>
            <button class="libra-cart-remove" type="button" data-libra-cart-remove aria-label="Remove">×</button>
          </article>
        `;
      })
      .join('');
  };

  const recommendationsForCart = async (cart) => {
    const products = await loadProducts();
    const cartHandles = new Set(cart.items.map((item) => item.handle));
    const cartProducts = cart.items.map((item) => productByHandle(products, item.handle) || item);
    const cartTypes = new Set(cartProducts.map(normalizedProductType).filter(Boolean));
    const category = cartRecommendationCategory(cart, products);
    const recommendations = products
      .filter((product) => !cartHandles.has(product.handle))
      .filter((product) => firstAvailableVariant(product))
      .sort(
        (a, b) =>
          Number(productCategory(a) !== category) - Number(productCategory(b) !== category) ||
          Number(!cartTypes.has(normalizedProductType(a))) - Number(!cartTypes.has(normalizedProductType(b))) ||
          String(a.title).localeCompare(String(b.title))
      )
      .slice(0, 8);

    return { title: categoryTitle(category), products: recommendations };
  };

  const renderRecommendations = async (cart) => {
    const container = qs(selectors.recs);
    if (!container) return;

    const recommendationGroup = await recommendationsForCart(cart);
    const recommendations = recommendationGroup.products;
    if (!recommendations.length) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    container.hidden = false;
    container.innerHTML = `
      <h3>${escapeHtml(recommendationGroup.title)}</h3>
      <div class="libra-cart-rec-list">
        ${recommendations
          .map((product) => {
            const variant = firstAvailableVariant(product);
            const image = normalizeProductImage(product);
            const price = centsFromShopifyPrice(variant.price);
            const compareAt = centsFromShopifyPrice(variant.compare_at_price);
            const saving = Math.max(0, compareAt - price);
            const compare = saving
              ? `<span class="libra-cart-rec-compare">${money(compareAt, cart.currency)}</span>`
              : '';
            const savingLine = saving
              ? `<span class="libra-cart-rec-saving">${escapeHtml(t('save').replace('{amount}', money(saving, cart.currency)))}</span>`
              : '';
            return `
              <article class="libra-cart-rec-card">
                <a href="${root()}products/${escapeHtml(product.handle)}">
                  ${
                    image
                      ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">`
                      : `<span aria-hidden="true"></span>`
                  }
                </a>
                <p class="libra-cart-rec-title">${escapeHtml(product.title)}</p>
                <p class="libra-cart-rec-price${saving ? ' has-discount' : ''}"><span class="libra-cart-sale-price">${money(price, cart.currency)}</span>${compare}</p>
                ${savingLine}
                <button class="libra-cart-rec-add" type="button" data-libra-cart-add="${variant.id}" data-libra-cart-product="${escapeHtml(
                  product.handle
                )}">
                  ${escapeHtml(t('add'))}
                </button>
              </article>
            `;
          })
          .join('')}
      </div>
    `;
  };

  const closeSizeSheet = () => {
    const existing = qs('[data-libra-cart-size-sheet]');
    if (existing) existing.remove();
    state.pendingUpsell = null;
    state.selectedUpsellVariantId = null;
  };

  const openSizeSheet = (productHandle, fallbackVariantId) => {
    const product = productByHandle(state.products || [], productHandle);
    const sizes = sizeVariantsForProduct(product).filter((entry) => entry.available);
    if (!product || sizes.length <= 1) {
      addVariant(fallbackVariantId || firstAvailableVariant(product)?.id);
      return;
    }

    closeSizeSheet();
    state.pendingUpsell = product;
    state.selectedUpsellVariantId = sizes[0]?.id || null;

    const sheet = document.createElement('div');
    sheet.className = 'libra-cart-size-sheet';
    sheet.setAttribute('data-libra-cart-size-sheet', '');
    sheet.innerHTML = `
      <button class="libra-cart-size-sheet-close" type="button" data-libra-cart-size-close aria-label="Close">×</button>
      <p class="libra-cart-size-sheet-title">${escapeHtml(t('size'))}:</p>
      <div class="libra-cart-size-options">
        ${sizes
          .map(
            (entry, index) => `
              <button class="libra-cart-size-option${index === 0 ? ' is-selected' : ''}" type="button" data-libra-cart-size-choice="${
              entry.id
            }">
                ${escapeHtml(entry.size)}
              </button>
            `
          )
          .join('')}
      </div>
      <button class="libra-cart-size-add" type="button" data-libra-cart-size-add>
        ${escapeHtml(t('addToBag'))}
      </button>
    `;
    qs(selectors.drawer)?.appendChild(sheet);
  };

  const applyCopy = () => {
    const title = qs(selectors.title);
    const loading = qs(selectors.loading);
    const subtotalLabel = qs(selectors.subtotalLabel);
    const shippingLabel = qs(selectors.shippingLabel);
    const shippingText = qs(selectors.shippingText);
    const delivery = qs(selectors.delivery);
    const returns = qs(selectors.returns);
    const checkoutText = qs(selectors.checkoutText);
    const emptyTitle = qs(selectors.emptyTitle);
    const emptyLink = qs(selectors.emptyLink);

    if (title) title.textContent = `${t('title')}${state.cart?.item_count ? ` (${state.cart.item_count})` : ''}`;
    if (loading) loading.textContent = t('loading');
    if (subtotalLabel) subtotalLabel.textContent = t('subtotal');
    if (shippingLabel) shippingLabel.textContent = t('shipping');
    if (shippingText) shippingText.textContent = t('shippingText');
    if (delivery) delivery.textContent = t('delivery');
    if (returns) returns.textContent = t('returns');
    if (checkoutText) checkoutText.textContent = t('checkout');
    if (emptyTitle) emptyTitle.textContent = t('emptyTitle');
    if (emptyLink) emptyLink.textContent = t('emptyLink');
  };

  const render = async (cart) => {
    applyCopy();
    updateCartBubble(cart);
    renderProgress(cart);

    const loading = qs(selectors.loading);
    const empty = qs(selectors.empty);
    const items = qs(selectors.items);
    const footer = qs(selectors.footer);
    const subtotal = qs(selectors.subtotal);
    const subtotalCompare = qs(selectors.subtotalCompare);
    const totalSaving = qs(selectors.totalSaving);
    const checkout = qs(selectors.checkout);
    const shippingText = qs(selectors.shippingText);

    if (loading) loading.hidden = true;

    if (!cart.item_count) {
      if (empty) empty.hidden = false;
      if (items) items.innerHTML = '';
      if (footer) footer.hidden = true;
      const recs = qs(selectors.recs);
      if (recs) {
        recs.hidden = true;
        recs.innerHTML = '';
      }
      return;
    }

    if (empty) empty.hidden = true;
    if (footer) footer.hidden = false;
    const products = await loadProducts();
    const originalTotal = originalCartTotal(cart, products);
    const totalSavings = Math.max(0, originalTotal - cart.total_price);
    if (footer) footer.classList.toggle('has-discount', Boolean(totalSavings));
    if (subtotal) subtotal.textContent = money(cart.total_price, cart.currency);
    if (shippingText) shippingText.textContent = t('shippingFree');
    if (subtotalCompare) {
      subtotalCompare.hidden = !totalSavings;
      subtotalCompare.textContent = totalSavings ? money(originalTotal, cart.currency) : '';
    }
    if (totalSaving) {
      totalSaving.hidden = !totalSavings;
      totalSaving.textContent = totalSavings
        ? t('save').replace('{amount}', money(totalSavings, cart.currency))
        : '';
    }
    if (checkout) checkout.href = `${root()}checkout`;

    renderItems(cart, products);
    await renderRecommendations(cart);
  };

  const refreshCart = async () => {
    const cart = await getCart();
    await render(cart);
  };

  const open = async (trigger) => {
    const element = shell();
    const drawer = qs(selectors.drawer);
    if (!element || !drawer) return;
    state.openedBy = trigger || document.activeElement;
    element.hidden = false;
    document.body.classList.add('libra-cart-open');
    requestAnimationFrame(() => element.classList.add('is-open'));
    await refreshCart();
    drawer.focus({ preventScroll: true });
  };

  const close = () => {
    const element = shell();
    if (!element) return;
    closeSizeSheet();
    element.classList.remove('is-open');
    document.body.classList.remove('libra-cart-open');
    window.setTimeout(() => {
      element.hidden = true;
      if (state.openedBy && typeof state.openedBy.focus === 'function') state.openedBy.focus({ preventScroll: true });
    }, 230);
  };

  const handleCartIcon = (event) => {
    const link = event.target.closest('#cart-icon-bubble, a[href$="/cart"], a[href="/cart"]');
    if (!link) return;
    event.preventDefault();
    open(link);
  };

  const isAddToCartForm = (form) =>
    Boolean(
      form &&
        form.matches('form[action*="/cart/add"]') &&
        (form.dataset.type === 'add-to-cart-form' ||
          form.querySelector('.product-variant-id') ||
          form.querySelector('[name="add"]'))
    );

  const formFromEvent = (event) => {
    const directForm = event.target.closest?.('form[action*="/cart/add"]');
    if (isAddToCartForm(directForm)) return directForm;

    const addButton = event.target.closest?.('[name="add"], .product-form__submit, .main-product-atc, [id^="SectionAtcBtn-"]');
    if (!addButton) return null;

    if (addButton.getAttribute('form')) {
      const form = document.getElementById(addButton.getAttribute('form'));
      if (isAddToCartForm(form)) return form;
    }

    const productForm = addButton.closest('product-form');
    const nestedForm = productForm?.querySelector('form[action*="/cart/add"]');
    if (isAddToCartForm(nestedForm)) return nestedForm;

    const mainForm = document.querySelector('product-form[data-main="true"] form[action*="/cart/add"]');
    return isAddToCartForm(mainForm) ? mainForm : null;
  };

  const submitCartForm = async (form, trigger) => {
    if (!form || form.dataset.libraCartNative === 'true' || state.busy) return;

    state.busy = true;
    try {
      try {
        await fetchJson('cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
      } catch (_error) {
        form.dataset.libraCartNative = 'true';
        form.requestSubmit();
        return;
      }

      await open(trigger || form.querySelector('[type="submit"]') || form);
    } finally {
      state.busy = false;
    }
  };

  const handleAddToCartClick = (event) => {
    const form = formFromEvent(event);
    if (!form || form.dataset.libraCartNative === 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    submitCartForm(form, event.target.closest('button, a, input') || form);
  };

  const handleAddToCartSubmit = async (event) => {
    const form = formFromEvent(event);
    if (!form || form.dataset.libraCartNative === 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    await submitCartForm(form, form.querySelector('[type="submit"]') || form);
  };

  const bindDrawerEvents = () => {
    document.addEventListener('click', handleCartIcon);
    document.addEventListener('click', handleAddToCartClick, true);
    document.addEventListener('submit', handleAddToCartSubmit, true);

    shell()?.addEventListener('click', (event) => {
      if (event.target.closest(selectors.close)) close();
      if (event.target.closest('[data-libra-cart-size-close]')) {
        closeSizeSheet();
        return;
      }

      const sizeChoice = event.target.closest('[data-libra-cart-size-choice]');
      if (sizeChoice) {
        state.selectedUpsellVariantId = sizeChoice.dataset.libraCartSizeChoice;
        qs('[data-libra-cart-size-sheet]')
          ?.querySelectorAll('[data-libra-cart-size-choice]')
          .forEach((button) => button.classList.toggle('is-selected', button === sizeChoice));
        return;
      }

      if (event.target.closest('[data-libra-cart-size-add]')) {
        const variantId = state.selectedUpsellVariantId;
        closeSizeSheet();
        addVariant(variantId);
        return;
      }

      const line = event.target.closest('[data-libra-cart-line]');
      if (!line) {
        const addButton = event.target.closest('[data-libra-cart-add]');
        if (addButton) openSizeSheet(addButton.dataset.libraCartProduct, addButton.dataset.libraCartAdd);
        return;
      }

      const key = line.dataset.libraCartLine;
      const item = state.cart?.items?.find((cartItem) => cartItem.key === key);
      if (!item) return;

      if (event.target.closest('[data-libra-cart-remove]')) {
        changeLine(key, 0);
        return;
      }

      const qtyButton = event.target.closest('[data-libra-cart-qty]');
      if (qtyButton) {
        const delta = Number(qtyButton.dataset.libraCartQty);
        changeLine(key, Math.max(0, item.quantity + delta));
      }
    });

    shell()?.addEventListener('change', (event) => {
      const sizeSelect = event.target.closest('[data-libra-cart-size-select]');
      if (sizeSelect) {
        const line = event.target.closest('[data-libra-cart-line]');
        const item = state.cart?.items?.find((cartItem) => cartItem.key === line?.dataset.libraCartLine);
        switchLineVariant(item, sizeSelect.value);
        return;
      }

      const input = event.target.closest('[data-libra-cart-qty-input]');
      const line = event.target.closest('[data-libra-cart-line]');
      if (!input || !line) return;
      changeLine(line.dataset.libraCartLine, Math.max(0, Number(input.value) || 0));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && shell()?.classList.contains('is-open')) close();
    });
  };

  const initTiffanyProductUi = () => {
    const productPath = '/products/turquoise-fluo-wide-leg-set';
    const currentPath = window.location.pathname.replace(/\/+$/, '');
    if (currentPath !== productPath || window.__albaTiffanyProductUi) return;
    window.__albaTiffanyProductUi = true;

    const soldOutVariantIds = new Set(['54381894861143', '54381894893911']);

    const render = () => {
      document.body.classList.add('alba-tiffany-stock-page');

      const rating = document.querySelector('.product__info-container .rating-stars');
      if (rating) {
        rating.style.setProperty('--rating', '4.6');
        const value = rating.querySelector('.alba-rating-value');
        if (value) value.textContent = '4.6';
      }

      const stock = document.getElementById('libra-stock');
      if (!stock) return;

      const variantInput =
        document.querySelector('.product__info-container form[action*="/cart/add"] [name="id"]') ||
        document.querySelector('form[action*="/cart/add"] [name="id"]');
      const variantId = String(variantInput?.value || '');
      const soldOut = soldOutVariantIds.has(variantId);
      const label = stock.querySelector('.libra-stock__text');
      const fill = stock.querySelector('.libra-stock__fill');

      stock.dataset.albaTiffanyStock = 'true';
      stock.classList.toggle('is-low', !soldOut);
      stock.classList.toggle('is-sold-out', soldOut);
      if (label) label.textContent = soldOut ? 'Sold out' : 'Almost sold out';
      if (fill) fill.style.width = soldOut ? '0%' : '13%';
    };

    const refresh = () => window.setTimeout(render, 50);
    render();
    document.addEventListener('change', refresh);
    document.addEventListener('variant:change', refresh);
    document.addEventListener('variantChange', refresh);
  };

  const init = () => {
    initTiffanyProductUi();
    if (!shell()) return;
    applyCopy();
    bindDrawerEvents();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.LibraCartDrawer = { open, close, refresh: refreshCart };
})();
